import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, UncontrolledTooltip } from 'reactstrap';
import { IImage } from '../../modules/reducers';
import theme from '../../assets/css/theme';
import { getTranslationKey } from '../../modules/utils';
import { FaExpandArrowsAlt, FaMobileAlt } from 'react-icons/fa';
import { omit } from 'lodash';
import styled from 'styled-components';
import media from '../../assets/css/media';
import { isMobile } from 'mobile-device-detect';
import { useImageCardActions } from './hooks/useImageCardActions';
import { formatCommentCount } from './hooks/useCommentCountBadge';

const ButtonsWrapper = styled(ButtonGroup)<{ $isPhotoswipe?: boolean }>`
  position: absolute;
  bottom: ${({ $isPhotoswipe }) => ($isPhotoswipe ? '0' : '30px')};
  z-index: 2;
  display: ${({ $isPhotoswipe }) => ($isPhotoswipe ? 'block' : 'none')};
  /* Buttons are inline-block, so without this they wrap onto a second row. */
  white-space: nowrap;

  button,
  a {
    width: ${({ $isPhotoswipe }) => ($isPhotoswipe ? '50px' : '25px')};
    height: ${({ $isPhotoswipe }) => ($isPhotoswipe ? '50px' : '25px')};

    i,
    svg {
      font-size: ${({ $isPhotoswipe }) => ($isPhotoswipe ? '18px' : '12px')};
      width: ${({ $isPhotoswipe }) => ($isPhotoswipe ? '18px' : '12px')};
      height: ${({ $isPhotoswipe }) => ($isPhotoswipe ? '18px' : '12px')};
      margin-top: 0 !important;
    }
  }
`;

const ButtonItem = styled(({ ...rest }) => {
  const btProps = omit(rest, 'isHidden');

  return <Button {...btProps} />;
})<{ isHidden: boolean }>`
  position: relative;
  background: ${theme.formsColors.secondBackground};
  color: ${theme.formsColors.secondColor};
  border: none;
  display: ${(props) => (props.isHidden ? 'none' : 'inline-flex')};
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const StyledTooltip = styled(UncontrolledTooltip)`
  // the tooltip has to sit above the photoswipe overlay (z-index: 1600)
  .tooltip {
    z-index: 1700;
  }

  .tooltip-inner {
    font-size: 12px;
    padding: 10px;
    max-width: 150px;
    word-wrap: break-word;
    text-align: center;
  }

  @media all and (max-width: ${media.desktop_min}) {
    display: none;
  }
`;

interface IActionTooltipProps {
  target: string;
  children: React.ReactNode;
}

// The action buttons used to be wired to `react-tooltip@4`, which is unmaintained and
// out of sync with React 18 event handling: its show/hide state machine could be left
// stale, so tooltips stayed on screen after the pointer left (window resize / DevTools
// toggle) or refused to open on a repeated hover in Safari. reactstrap's tooltip drives
// the same behaviour, is already used across the app and stays in sync with the target.
// reactstrap resolves a string `target` with a document lookup during render
// (PopperContent.setTargetNode) and throws when the id is not in the DOM. In
// the photoswipe overlay the button ids include the image id, so deleting the
// currently shown image re-renders the open tooltip with the next image's id
// while the DOM still holds the previous one — the failed lookup crashed the
// whole app to a white screen. Resolving the node after commit and passing the
// element itself bypasses reactstrap's lookup entirely (elements are used
// as-is), and the `key` remounts the tooltip when the target id changes so its
// hover listeners re-attach to the right node.
const ActionTooltip = ({ target, children }: IActionTooltipProps) => {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setTargetElement(document.getElementById(target));
  }, [target]);

  if (isMobile || !targetElement) return null;

  return (
    <StyledTooltip
      key={target}
      target={targetElement}
      trigger="hover"
      data-cy="image-action-tooltip"
    >
      {children}
    </StyledTooltip>
  );
};

// Small count sitting on the corner of the comments button, so the toolbar shows
// how many comments an image has without opening the modal.
const CommentsCountPill = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  min-width: 14px;
  padding: 0 3px;
  border-radius: 7px;
  background: ${theme.formsColors.secondColor};
  color: ${theme.formsColors.secondBackground};
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
  pointer-events: none;
`;

export type ActionButtonsTypes =
  | 'title'
  | 'smallScreenTitle'
  | 'crop'
  | 'info'
  | 'download'
  | 'watermark'
  | 'delete'
  | 'fullscreen'
  | 'comments';

interface IProps {
  image: IImage;
  index?: number;
  hideButtons?: ActionButtonsTypes[];
  actionID?: string;
  galleriesImages: IImage[];
  isPhotoswipe?: boolean;
}

export const ImageCardActions = ({
  image,
  hideButtons = [],
  actionID,
  galleriesImages,
  isPhotoswipe
}: IProps) => {
  const { originalImageName } = image;
  const {
    ids,
    downloadHref,
    commentCount,
    showComments,
    checkIsButtonHidden,
    setTitleImageHandler,
    setSmallScreenTitleImageHandler,
    deleteImageHandler,
    setWatermarkHandler,
    cropImageHandler,
    openCommentsHandler,
    onOpenInFullscreen,
    preventPropagation
  } = useImageCardActions({ image, hideButtons, actionID, galleriesImages, isPhotoswipe });

  return (
    <ButtonsWrapper
      onClick={preventPropagation}
      className="image-tools"
      $isPhotoswipe={isPhotoswipe}
    >
      <ButtonItem
        id={ids.title}
        size="sm"
        color="primary"
        onClick={setTitleImageHandler}
        isHidden={checkIsButtonHidden('title')}
      >
        <i className="icon-picture" />
        <ActionTooltip target={ids.title}>
          {getTranslationKey('gallerySettings.imageActions.titleImage')}
        </ActionTooltip>
      </ButtonItem>
      <ButtonItem
        id={ids.smallScreenTitle}
        size="sm"
        color="primary"
        onClick={setSmallScreenTitleImageHandler}
        isHidden={checkIsButtonHidden('smallScreenTitle')}
      >
        <FaMobileAlt />
        <ActionTooltip target={ids.smallScreenTitle}>
          {getTranslationKey('gallerySettings.imageActions.smallScreenTitleImage')}
        </ActionTooltip>
      </ButtonItem>
      <ButtonItem
        id={ids.crop}
        size="sm"
        color="primary"
        onClick={cropImageHandler}
        isHidden={checkIsButtonHidden('crop')}
      >
        <i className="icon-frame" />
        <ActionTooltip target={ids.crop}>
          {getTranslationKey('gallerySettings.imageActions.appIcon')}
        </ActionTooltip>
      </ButtonItem>
      {!isPhotoswipe && (
        <ButtonItem
          id={ids.fullscreen}
          size="sm"
          color="primary"
          onClick={onOpenInFullscreen}
          isHidden={checkIsButtonHidden('fullscreen')}
        >
          <FaExpandArrowsAlt />
          <ActionTooltip target={ids.fullscreen}>
            {getTranslationKey('gallerySettings.imageActions.fullscreen')}
          </ActionTooltip>
        </ButtonItem>
      )}
      {!isPhotoswipe && (
        <ButtonItem id={ids.info} size="sm" color="primary" isHidden={checkIsButtonHidden('info')}>
          <i data-cy="icon-info" className="icon-info" />
          <ActionTooltip target={ids.info}>{originalImageName}</ActionTooltip>
        </ButtonItem>
      )}
      <ButtonItem
        id={ids.download}
        size="sm"
        color="primary"
        href={downloadHref}
        isHidden={checkIsButtonHidden('download')}
      >
        <i className="icon-cloud-download" />
        <ActionTooltip target={ids.download}>
          {getTranslationKey('downloadOriginalImage')}
        </ActionTooltip>
      </ButtonItem>
      <ButtonItem
        id={ids.watermark}
        size="sm"
        color="primary"
        onClick={setWatermarkHandler}
        isHidden={checkIsButtonHidden('watermark')}
      >
        <i className="icon-drop" />
        <ActionTooltip target={ids.watermark}>
          {getTranslationKey('watermark.watermark')}
        </ActionTooltip>
      </ButtonItem>
      {showComments && (
        <ButtonItem
          id={ids.comments}
          size="sm"
          color="primary"
          onClick={openCommentsHandler}
          data-cy={`image-comments-action-${image._id}`}
          isHidden={checkIsButtonHidden('comments')}
        >
          <i className="icon-bubble" />
          <CommentsCountPill>{formatCommentCount(commentCount)}</CommentsCountPill>
          <ActionTooltip target={ids.comments}>
            {getTranslationKey('collectionSettings.comments.badgeTooltip', {
              count: commentCount
            })}
          </ActionTooltip>
        </ButtonItem>
      )}
      <ButtonItem
        id={ids.delete}
        size="sm"
        color="danger"
        onClick={deleteImageHandler}
        isHidden={checkIsButtonHidden('delete')}
      >
        <i className="icon-trash" data-cy="icon-trash" />
        <ActionTooltip target={ids.delete}>
          {getTranslationKey('gallerySettings.imageActions.delete')}
        </ActionTooltip>
      </ButtonItem>
    </ButtonsWrapper>
  );
};
